'use strict';

const _ = require('lodash');
const Promise = require('bluebird');
const EventEmitter = require('wolfy87-eventemitter');
const Connection = require('./Connection');
const Events = require('./Events');
const Chat = require('./Chat');
const Api = require('./Api');
const Helper = require('./Helper');
const Logger = require('./Logger');

let _LocalChannel = null;

/**
 * The lobby is the chat handler for each user. It resembles to a chat inbox.
 * Every user has its own lobby, and it can be linked with one or more "rooms".
 * A lobby is capable of:
 *     - receive new invitations
 *     - add new chats
 *     - handle 'room' subscription
 *     - bind to this user's chats join and leave events
 *     - change user data and/or status
 *     - retrieve user's active chats
 */
class Lobby extends EventEmitter {
    /**
     * Initializes all Lobby properties to their default values
     * @return {Lobby} A new Lobby instance
     */
    constructor () {
        super();
        // which rooms the Lobby is subscribed to
        this.rooms = [];
        // chats that are currently active (Chat objects)
        this.chats = {};
        // chats that are currently active in watch mode (Chat objects)
        this.chatsWatching = {};
        // chat invitations received (server objects)
        this.chatInvitations = {};
        // holds whether the user is logged or not
        this.logged = false;
        // control if this Lobby has to receive chat invitations or not
        this.invitations = true;
        // lobby listening event names
        this.listeningEvents = [];
        // assignation interval for an invitation
        this.assignationInterval = 0;
        if (global.window) {
            global.window.onfocus = () => {
                Connection.getConnection() && Events.send('user:focus');
            };
        }
    }

    configure (params) {
        _LocalChannel = params.LocalChannel;
    }

    /**
     * Initializates a lobby for a user
     * @param {Object}  data   User auth information.
     *                         Has to contain: id (= userId), token (= session.token), [optional] rooms (= rooms to connect to)
     */
    init (data = {}) {
        if (this.logged) {
            return Promise.resolve();
        }
        return Connection.open(data)
            .then((user) => {
                this.logged = true;
                this.rooms = user.rooms;
                // update the invitations control
                if (data.hasOwnProperty('invitations')) {
                    this.invitations = data.invitations;
                }
                // add user chat invitations to lobby, if any
                if (user.invitations) {
                    this.chatInvitations = user.invitations;
                }

                return this.initUserChats(user);
            })
            .then(() => Api.request('/settings', 'GET'))
            .then(({data}) => {
                if (
                    data &&
                    data.settings &&
                    data.settings.chat &&
                    data.settings.chat.assignation &&
                    data.settings.chat.assignation.interval &&
                    !(data.settings.queue && data.settings.queue.active)
                ) {
                    this.assignationInterval = data.settings.chat.assignation.interval;
                }
            })
            .then(() => this.initEvents())
            .then(() => this.checkListeningEvents());
    }

    checkListeningEvents () {
        if (this.logged) {
            const mustHave = [
                'user:invite',
                'invitations:reject:success',
                'invitation:expired',
                'invitations:reject',
                'chat:remove',
                'chats:leave:success',
                'system:info',
                'user:join'
            ];
            let missing = [];
            mustHave.forEach((event) => {
                if (Connection.listeningEvents.indexOf(event) === -1) {
                    missing.push(event);
                }
            });
            if (missing.length > 0) {
                this.initEvents(missing);
            }
            Object.keys(this.chats).forEach((key) => {
                this.chats[key].checkListeningEvents();
            });
            setTimeout(this.checkListeningEvents.bind(this), 10000);
        }
    }

    /**
     * Initialize any chats the user might already have
     * @param {Object} user The user which chats will be initialized
     */
    initUserChats (user) {
        // add user chats to lobby, if any
        const userHasChatsWatching = user.chatsWatching && !_.isEmpty(user.chatsWatching);
        let activeChats = user.chats;

        if (userHasChatsWatching) {
            activeChats = Object.assign(activeChats, user.chatsWatching);
        }

        if (activeChats) {
            const chatsKeys = Object.keys(activeChats);
            return Promise.map(chatsKeys, (chatId) => {
                return Chat.init({ id: chatId })
                    .then((chatInstance) => {
                        var mode = '';
                        if (userHasChatsWatching && chatId in user.chatsWatching) {
                            mode = 'watch';
                        }
                        this.addChat(chatInstance, mode);
                        return chatInstance.id;
                    });
            });
        }
        return Promise.resolve();
    }

    /**
     * Loads (init + join) the given chats as a watcher and triggers chats:watch
     * @param {Array}  chatIds   Chat IDs
     */
    loadChatsWatching (chatIds) {
        const chatInits = [];

        for (let chatId of chatIds) {
            if (!this.isChatWatching(chatId)) {
                const promise = Chat.init({ id: chatId })
                    .then((chatInstance) => {
                        return chatInstance.join('watch')
                            .then(() => this.addChat(chatInstance, 'watch'));
                    });
                chatInits.push(promise);
            }
        }

        return Promise.all(chatInits)
            .then(() => this.trigger('chats:watch', [{ chats: this.chatsWatching }]));
    }

    /**
     * Starts watching the given chat IDs
     * @param {Array} chatIds   Chat IDs
     */
    watch (chatIds) {
        _LocalChannel.loadChatsWatching(chatIds);
        this.loadChatsWatching(chatIds);
    }

    /**
     * Join the given room
     * @param  {String}  roomId  Room ID
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    joinRoom (roomIds, roomStatus=null) {
        const data = {
            roomIds: Array.isArray(roomIds) ? roomIds : [roomIds],
            userId: Connection.me().id
        };
        if (roomStatus) {
            data.roomStatus = roomStatus;
        }
        return Events.send('rooms:join', data)
            .then((res) => {
                for (let roomId of res.joinedRooms) {
                    this.addRoom(roomId);
                }
                return null;
            });
    }

    /**
     * Leave the given room
     * @param  {String} roomId Room ID
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    leaveRoom (roomIds) {
        const data = {
            roomIds: Array.isArray(roomIds) ? roomIds : [roomIds],
            userId: Connection.me().id
        };
        return Events.send('rooms:leave', data)
            .then((res) => {
                for (let roomId of res.leftRooms) {
                    this.removeRoom(roomId);
                }
                return null;
            });
    }

    /**
     * Add the room ID to the rooms array
     * @param  {String} roomId Room ID
     */
    addRoom (roomId) {
        if (!roomId) {
            return;
        }
        roomId = roomId.toString();
        // update the cookie info (if required)
        const cookie = Connection.getCookie();
        if (cookie) {
            cookie.rooms.push(roomId);
            Connection.setCookie(cookie);
        }
        if (this.rooms.indexOf(roomId) === -1) {
            this.rooms.push(roomId);
        }
    }

    /**
     * Remove the room ID from the rooms array
     * @param  {String} roomId Room ID
     */
    removeRoom (roomId) {
        if (!roomId) {
            return;
        }
        roomId = roomId.toString();
        // update the cookie info (if required)
        const cookie = Connection.getCookie();
        if (cookie) {
            cookie.rooms.splice(cookie.rooms.indexOf(roomId), 1);
            Connection.setCookie(cookie);
        }
        const pos = this.rooms.indexOf(roomId);
        return (pos > -1) && this.rooms.splice(pos, 1);
    }

    /**
     * Adds a chat to the user chats array
     * @param {Chat}   chat
     * @param {string} mode
     */
    addChat (chat, mode = '') {
        const chatProto = chat.__proto__ || Object.getPrototypeOf(chat);
        if (chatProto === Chat.prototype) {
            if (mode === 'watch') {
                this.chatsWatching[chat.id] = chat;
            } else {
                this.chats[chat.id] = chat;
            }
            return chat;
        }
    }

    /**
     * Removes a chat from the user chats array
     * @param {String} chatId
     */
    removeChat (chatId) {
        const isWatching = this.isChatWatching(chatId);
        const chat = isWatching ? this.chatsWatching[chatId] : this.chats[chatId];

        if (chat) {
            const chatProto = chat.__proto__ || Object.getPrototypeOf(chat);
            if (chatProto === Chat.prototype) {
                chat.unsubscribe();
            }
        }
        if (isWatching) {
            delete this.chatsWatching[chatId];
        } else {
            delete this.chats[chatId];
        }
    }

    /**
     * Leaves the specified chat
     * @param  {String} chatId id of the Chat to leave
     * @return {Promise<Chat|Error>} A Promise fulfilled with { chatId, userId }
     */
    leaveChat (chatId) {
        const isWatching = this.isChatWatching(chatId);
        const chat = isWatching ? this.chatsWatching[chatId] : this.chats[chatId];
        if (!chat) {
            // If there's no such chat, just resolve because the user
            // won't be on it, which is what (s)he wanted from the beginning
            return Promise.resolve();
        }
        this.removeChat(chatId);
        const mode = isWatching ? 'watch' : false;
        return chat.leave(mode); // Send event to server
    }

    /**
     * Intervenes a chat
     * @param  {String} chatId id of the Chat to leave
     */
    interveneChat (chatId) {
        const chatWatching = this.chatsWatching[chatId];
        if (!chatWatching) {
            throw new Error('The chat doesn\'t exist');
        }
        return chatWatching.intervene()
            .then(() => {
                // Swap chat between chatsWatching and chats
                // but don't unsubscribe from its events
                delete this.chatsWatching[chatId];
                this.addChat(chatWatching);
                return chatWatching;
            });
    }

    /**
     * Adds a chat to the user chatInvitations  array
     * @param {Chat} chat Chat to be added to invitations
     */
    addChatInvitation (chat) {
        if (chat.id) {
            this.chatInvitations[chat.id] = chat;
        }
    }

    /**
     * Adds a chat to the user chatInvitations  array
     * @param {String} chatId id of the chat to be removed
     */
    removeChatInvitation (chatId) {
        delete this.chatInvitations[chatId];
    }

    /**
     * Send an invitation acceptance event
     * @param  {Object} chat Chat info object
     * @return {Promise<Chat|Error>} A Promise fulfilled with the Chat that has just been accepted
     * or an Error in case the invitation couldn't be accepted for some reason
     */
    acceptInvitation (chatId) {
        this.chatInvitations[chatId].accepted = true;

        return Events.send('invitations:accept', { userId: Connection.me().id, chatId: chatId })
            .then((res) => {
                if (res.success || res.message === 'chat_closed') {
                    return Chat.init({ id: chatId })
                        .then((chatInstance) => {
                            this.removeChatInvitation(chatInstance.id);
                            if (!chatInstance.closed) {
                                this.addChat(chatInstance);
                            }
                            // Use this workaround to remove functions from Chat object.
                            // If there are functions, BroadcastChannel cannot postMessage.
                            const chat = JSON.parse(JSON.stringify(chatInstance));
                            _LocalChannel.acceptInvitation({ chat });
                            return chatInstance;
                        });
                } else {
                    throw new Error('Invitation was not accepted');
                }
            });
    }

    /**
     * Send an invitation rejection event
     * @param  {Chat} chat      Chat info object
     * @return {Promise<Object|Error>} A Promise fulfilled with the data coming from the server
     */
    rejectInvitation (chatId) {
        this.removeChatInvitation(chatId);
        return Events.send('invitations:reject', { userId: Connection.me().id, chatId: chatId });
    }

    /**
     * Gets the user active chats
     * @return {Array} Active chats array
     */
    getUserChats () {
        return this.chats;
    }

    /**
     * Disconnect from server and remove all rooms
     */
    close () {
        if (this.logged) {
            this.rooms = [];
            this.listeningEvents = [];
            this.removeAllListeners();
            Connection.close();
            this.logged = false;
        }
    }

    /**
     * Executes when a chat invitation is received
     * @param  {Object} user Invitation recipient (may or may not be the lobby's user)
     * @param  {Object} chat Chat to which the user is invited
     */
    onInvite (data) {
        const chatId = data.chat.id;

        const handleInvitationExpireProperty = () => {
            // assign invitationExpiration property if not set before
            // if the sender is not system, it is an explicit invitation that does not expire
            if (!data.chat.invitationExpiration && this.assignationInterval && data.sender === 'system') {
                data.chat.invitationExpiration = Helper.getUnixTime() + this.assignationInterval;
            }

            if (data.chat.invitationExpiration) {
                // recalculate invitationExpiration considering the browser's time and ping
                data.chat.invitationExpiration = Helper.getBrowserExpirationTime(data.chat.invitationExpiration, Connection.timeDifferenceWithServer, Connection.ping);
            }
        };

        // check if the invitation is for the current user and he is not already in the chat
        const notInChat = !this.chats[chatId] && !this.chatInvitations[chatId];
        if (Connection.me().id === data.userId && notInChat) {
            // forced invitation
            if (data.forced) {
                Chat.init({ id: chatId })
                    .then((chatInstance) => {
                        // update chat data
                        data.chat = chatInstance;
                        handleInvitationExpireProperty();
                        this.addChat(chatInstance);
                        this.trigger('chat:invitation', [data]);
                    });
            } else {
                handleInvitationExpireProperty();
                this.addChatInvitation(data.chat);
                this.trigger('chat:invitation', [data]);
            }
        }
    }

    /**
     * Executes when a chat should be removed
     * @param  {Object} data Event data, which contains userId and chatId
     */
    onChatRemove (data) {
        if (data.userId === Connection.me().id) {
            this.removeChat(data.chatId);
            this.trigger('chat:remove', [data]);
        }
    }

    /**
     * Binds essential server events
     * @param  {array}      missing If missing events are specified, these are the only ones to init, else init them all
     * @return {Promise}
     */
    initEvents (missing = null) {
        let subscriptions = [];

        if (!missing || (missing.indexOf('user:invite') !== -1)) {
            // Listen to invitations (which will arrive just when being online)
            const userInvitePromise = Events.subscribe('user:invite', (data) => {
                this.onInvite(data);
            });
            subscriptions.push(userInvitePromise);
        }

        if (!missing || (missing.indexOf('invitation:expired') !== -1)) {
            // Listen for invitations that have expired
            const invitationExpiredPromise = Events.subscribe('invitation:expired', (data) => {
                if (this.chatInvitations[data.chatId]) {
                    data.accepted = this.chatInvitations[data.chatId].accepted;
                } else {
                    data.accepted = false;
                }
                this.removeChatInvitation(data.chatId);
                this.trigger('invitation:expired', [data]);
            });
            subscriptions.push(invitationExpiredPromise);
        }

        if (!missing || (missing.indexOf('invitations:reject') !== -1)) {
            // Listen for rejected invitations for chats we're in
            const invitationRejectedPromise = Events.subscribe('invitations:reject', (data) => {
                if (data.chatId && this.chats[data.chatId]) {
                    this.trigger('invitations:reject', [data]);
                }
            });
            subscriptions.push(invitationRejectedPromise);
        }

        if (!missing || (missing.indexOf('chat:remove') !== -1)) {
            // Fired whenever the user has to remove the chat (because he/she transfered it, for example)
            const chatRemovePromise = Events.subscribe('chat:remove', (data) => {
                this.onChatRemove(data);
            });
            subscriptions.push(chatRemovePromise);
        }

        if (!missing || (missing.indexOf('system:info') !== -1)) {
            // Listen for system:info events and just propagate them
            const systemInfoPromise = Events.subscribe('system:info', (data) => {
                this.trigger('system:info', [data]);
            });
            subscriptions.push(systemInfoPromise);
        }

        if (!missing || (missing.indexOf('chats:leave:success') !== -1)) {
            // Listen for toInbox on other connections
            const myselfLeavePromise = Events.subscribe('chats:leave:success', (data) => {
                this.onChatRemove(data);
            });
            subscriptions.push(myselfLeavePromise);
        }

        if (!missing || (missing.indexOf('invitations:reject:success') !== -1)) {
            // Listen for rejections on other connections
            const myselfRejectPromise = Events.subscribe('invitations:reject:success', (data) => {
                if (data.success && data.userId === Connection.me().id && this.chatInvitations[data.chatId]) {
                    this.removeChatInvitation(data.chatId);
                    // Simulate that the invitation has expired so that the UI can remove it
                    this.trigger('invitation:expired', [data]);
                }
            });
            subscriptions.push(myselfRejectPromise);
        }

        if (!missing || (missing.indexOf('logout') !== -1)) {
            // Listen for a logout from the user
            const logoutPromise = Events.subscribe('logout', () => {
                this.trigger('logout');
            });
            subscriptions.push(logoutPromise);
        }

        if (!missing || (missing.indexOf('ACCEPT_INVITATION') !== -1)) {
            // Listen for a logout from the user
            const acceptInvitationPromise = _LocalChannel.on('ACCEPT_INVITATION', (data) => {
                if (data.chat && this.chatInvitations[data.chat.id]) {
                    this.removeChatInvitation(data.chat.id);
                    return Chat.init({ id: data.chat.id })
                        .then((chatInstance) => {
                            this.addChat(chatInstance);
                            data.chat = chatInstance;
                        })
                        .then(() => this.trigger('chat:joined', [data]));
                }
            });
            subscriptions.push(acceptInvitationPromise);
        }

        if (!missing || (missing.indexOf('LOAD_CHATS_WATCHING') !== -1)) {
            const loadChatsWatchingPromise = _LocalChannel.on('LOAD_CHATS_WATCHING', (data) => {
                return this.loadChatsWatching(data);
            });
            subscriptions.push(loadChatsWatchingPromise);
        }

        return Promise.all(subscriptions);
    }

    /**
     * Add a listener to a Lobby event
     * @param  {String} event name
     * @param  {Object} callbacks Callback functions (optional)
     */
    addEventListener (type, callback) {
        if (this.listeningEvents.indexOf(type) !== -1) {
            Logger.warn('Already listening to ' + type);
            return;
        }
        this.listeningEvents.push(type);
        this.on(type, callback);
    }

    /**
     * Returns if a chat is in watching or not
     * @param  {String} chatId  chat ID
     * @param  {Bool}
     */
    isChatWatching(chatId) {
        return chatId in this.chatsWatching;
    }
}

module.exports = new Lobby();



// WEBPACK FOOTER //
// ./src/Lobby.js